import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom/cjs/react-router-dom.min";
import { Drawer, Avatar, Input, message, Dropdown, notification } from "antd";
import { CameraFilled, CloseOutlined, } from "@ant-design/icons";
import PhoneInput from "react-phone-number-input";
import PhoneCountrySelect from 'components/PhoneCountrySelect';
import { useKeepPhoneDigitsOnCountryChange, isIndianPhone, splitPhone, keepCallingCodeOnKeyDown } from 'components/phoneCountryUtils';
import "react-phone-number-input/style.css";

import { Button } from "components/Button";
import { addStaff, addStaffSuccess, updateStaffProfile, uploadStaffPhoto, deleteStaffPhoto, getStaffListV3, getStaffStats, extractErrorMessage } from "actions/userManagementV3";
import { setUserDetails } from "actions/userManagementV2";
import { getTeachersGeofenceLocations } from "actions/attendanceNew";
import UserRoleSelectV3 from "pages/userManagementV3/userRoleSelectV3";
import AddStaffSuccessModal from "./addStaffSuccessModal";
import UserProfilePicChangeModal from "pages/userManagementV2/userProfilePicChangeModal";
import "../styles/addStaffDrawer.css";
import FloatingLabelInput from "../utils/floatingLabelInput";
import { sanitizeName } from "../utils/formatters";
import UserDefault from "../../../Assets/userManagementV3/userDefault.png";
import CreateAccessDrawer from "pages/userManagementV3/localComponent/createAccessDrawer";

const roles = [
    { label: "Teacher", value: "Teacher", },
    { label: "Operations", value: "Operation", },
];

const ACCESS_DATA = [
    "Staff manager",
    "Student manager",
    "Fee manager",
    "Operations admin",
    "Academic admin",
];

const defaultForm = {
    role: [],
    name: "",
    mobile: "+91",
    email: "",
    profile: "",
    access: "",
    geofence: "Global",
    geofenceId: 1,
    // permissions: permissions.reduce((acc, item) => {
    //     acc[item] = {
    //         checked: false,
    //         access: "",
    //     };
    //     return acc;
    // }, {}),
};

const mobileRegex = /^[6-9]\d{9}$/;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const AddStaffDrawer = ({ visible, onClose, editingStaff = null, onStaffAdded }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const basePath = location.pathname.startsWith('/user-management-v2') ? '/user-management-v2' : '/user-management';
    const isEditMode = !!editingStaff;

    const [step, setStep] = useState("form");
    const [form, setForm] = useState(defaultForm);
    const [successVisible, setSuccessVisible] = useState(false);
    const [createdStaff, setCreatedStaff] = useState(null);
    const [profileFile, setProfileFile] = useState(null);
    const [profilePicModalOpen, setProfilePicModalOpen] = useState(false);
    const [stagedImgUrl, setStagedImgUrl] = useState(null);
    const [stagedImageFile, setStagedImageFile] = useState(null);
    const [pendingPhotoRemoval, setPendingPhotoRemoval] = useState(false);
    const inputProfilePicRef = React.useRef(null);
    const [submitted, setSubmitted] = useState(false);
    const [touched, setTouched] = useState({ mobile: false, email: false });
    const [accessDropdownOpen, setAccessDropdownOpen] = useState(false);
    const [createAccessOpen, setCreateAccessOpen] = useState(false);
    const [geofenceDropdownOpen, setGeofenceDropdownOpen] = useState(false);
    const [geoFenceList, setGeoFenceList] = useState([]);
    const [geoFenceLoading, setGeoFenceLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const teachersGeofenceLocDataContainer = useSelector(
        state => state?.attendanceNew?.teachersGeofenceLocDataContainer
    );

    useEffect(() => {
        if (!visible) return;
        setGeoFenceLoading(true);
        dispatch(getTeachersGeofenceLocations((res, err) => {
            setGeoFenceLoading(false);
            if (res) {
                const list = Array.isArray(res)
                    ? res
                    : (Array.isArray(res?.data) ? res.data : (Array.isArray(res?.result) ? res.result : []));
                if (list && list.length > 0) {
                    setGeoFenceList(list);
                }
            }
        }));
    }, [visible, dispatch]);

    useEffect(() => {
        if (Array.isArray(teachersGeofenceLocDataContainer) && teachersGeofenceLocDataContainer.length > 0) {
            setGeoFenceList(teachersGeofenceLocDataContainer);
        }
    }, [teachersGeofenceLocDataContainer]);

    // Automatically link geofenceId for "Global" once location data loads
    useEffect(() => {
        const list = (Array.isArray(geoFenceList) && geoFenceList.length > 0)
            ? geoFenceList
            : (Array.isArray(teachersGeofenceLocDataContainer) ? teachersGeofenceLocDataContainer : []);
        if (list.length > 0 && (form.geofence || "").trim().toLowerCase() === "global" && !form.geofenceId) {
            const globalLoc = list.find(item => (item?.title || item?.geoFenceTitle || "").trim().toLowerCase() === "global");
            if (globalLoc) {
                updateField("geofenceId", globalLoc.id ?? globalLoc.geoFenceId);
            }
        }
    }, [geoFenceList, teachersGeofenceLocDataContainer, form.geofence, form.geofenceId]);

    const dropdownLocations = useMemo(() => {
        const list = (Array.isArray(geoFenceList) && geoFenceList.length > 0)
            ? geoFenceList
            : (Array.isArray(teachersGeofenceLocDataContainer) ? teachersGeofenceLocDataContainer : []);

        const hasGlobal = list.some(item => (item?.title || item?.geoFenceTitle || "").trim().toLowerCase() === "global");
        return hasGlobal ? list : [{ id: 1, title: "Global" }, ...list];
    }, [geoFenceList, teachersGeofenceLocDataContainer]);

    useEffect(() => {
        if (!visible) return;
        if (editingStaff) {
            setStep("form");
            setSubmitted(false);
            setTouched({ mobile: false, email: false });
            setSuccessVisible(false);
            setProfileFile(null);
            setPendingPhotoRemoval(false);
            setStagedImgUrl(null);
            setStagedImageFile(null);
            setProfilePicModalOpen(false);
            const phoneVal = editingStaff.phone || "";
            const initialPhone = phoneVal ? (phoneVal.startsWith("+") ? phoneVal : `+91${phoneVal}`) : "+91";
            const initialPhoto = editingStaff.image || editingStaff.avatar || "";
            setForm({
                ...defaultForm,
                role: Array.isArray(editingStaff.role) ? editingStaff.role : (editingStaff.role ? [editingStaff.role] : []),
                name: editingStaff.name || "",
                mobile: initialPhone,
                email: editingStaff.email || "",
                profile: initialPhoto,
                access: editingStaff.access || "",
                geofence: editingStaff.geofence || editingStaff.geoFenceTitle || editingStaff.geofenceTitle || "Global",
                geofenceId: editingStaff.geofenceId || editingStaff.geoFenceId || null,
            });
        } else {
            setStep("form");
            setForm({ ...defaultForm, mobile: "+91" });
            setPendingPhotoRemoval(false);
            setStagedImgUrl(null);
            setStagedImageFile(null);
            setProfilePicModalOpen(false);
            resetDrawer();
        }
    }, [visible, editingStaff]);

    const updateField = (key, value) => {
        setForm(prev => ({
            ...prev,
            [key]: value,
        }));
    };

    // Mobile number: keep the typed number when the country is changed.
    const mobileCountryHandlers = useKeepPhoneDigitsOnCountryChange(form.mobile, (val) => {
        updateField("mobile", val || "");
        setTouched(prev => ({ ...prev, mobile: true }));
    });

    const initials = useMemo(() => {

        if (!form.name) return "";

        return form.name
            .trim()
            .split(" ")
            .map(i => i[0])
            .join("")
            .substring(0, 2)
            .toUpperCase();

    }, [form.name]);

    const beforeUpload = file => {

        const isImage = file.type.startsWith("image/");

        if (!isImage) {
            message.error("Only image files are allowed.");
            return Upload.LIST_IGNORE;
        }

        const isLessThan5MB =
            file.size / 1024 / 1024 < 5;

        if (!isLessThan5MB) {

            message.error(
                "File size exceeds 5MB."
            );

            return Upload.LIST_IGNORE;
        }

        setProfileFile(file);

        const reader = new FileReader();

        reader.onload = e => {

            updateField(
                "profile",
                e.target.result
            );

        };

        reader.readAsDataURL(file);

        return false;
    };

    // form.mobile now holds the full PhoneInput value (e.g. "+919977788997",
    // country included) rather than a bare 10-digit string. Stripping the
    // "+91"/"91" prefix off the FRONT (not slice(-10) off the end) matters
    // while it's still being typed: the total digit count (country code +
    // partial national number) can coincidentally equal 10 before the
    // national number itself is actually 10 digits (e.g. "91" + 8 digits =
    // 10), so taking the last 10 digits can grab part of the country code as
    // if it were the number and pass validation by accident. Stripping the
    // known prefix instead always leaves the real national number, whatever
    // its current length — same fix applied to userEditProfileV3.js/
    // addStudent.js's identical mobileDigits check.
    const mobileDigits = (form.mobile || "").startsWith('+91') ? form.mobile.slice(3)
        : (form.mobile || "").startsWith('91') ? form.mobile.slice(2)
            : (form.mobile || "").replace(/\D/g, "");
    // Only Indian (+91) numbers are format-validated (as before); for any
    // other country a number just needs to be entered.
    const typedMobileDigits = splitPhone(form.mobile).digits;
    const errors = {
        role: form.role.length === 0,
        name: !form.name.trim(),
        mobile: isIndianPhone(form.mobile) ? !mobileRegex.test(mobileDigits) : typedMobileDigits.length === 0,
        email: form.email && !emailRegex.test(form.email),
    };

    const isFormValid = !errors.role && !errors.name && !errors.mobile && !errors.email;

    const isFormEmpty = useMemo(() => {
        if (isEditMode) return false;
        const hasRole = Array.isArray(form.role) && form.role.length > 0;
        const hasName = Boolean(form.name && form.name.trim());
        const nationalMobileDigits = (form.mobile || "").replace(/^\+91|^91/, "").replace(/\D/g, "");
        const hasMobile = nationalMobileDigits.length > 0;
        const hasEmail = Boolean(form.email && form.email.trim());
        const hasProfile = Boolean(profileFile || form.profile);
        const hasAccess = Boolean(form.access && form.access.trim());
        const hasGeofence = Boolean(form.geofence && form.geofence !== "Global" && form.geofenceId !== 1);

        return !hasRole && !hasName && !hasMobile && !hasEmail && !hasProfile && !hasAccess && !hasGeofence;
    }, [form, profileFile, isEditMode]);

    const clearForm = () => {
        setSubmitting(false);
        setSubmitted(false);
        setTouched({ mobile: false, email: false });
        setProfileFile(null);
        setForm({
            ...defaultForm,
            mobile: "+91",
        });
    };

    const handleAdd = () => {
        setSubmitted(true);
        if (!isFormValid) return;
        setStep("preview");
    };

    const handleEdit = () => {
        setStep("form");
    };

    const handleConfirm = () => {
        if (submitting) return;

        const params = {
            name: form.name,
            phone: form.mobile,
            roles: form.role,
        };

        if (form.email) {
            params.email = form.email;
        }

        if (form.access) {
            params.privilages = [form.access];
        }

        if (form.geofenceId) {
            params.geoFenceId = form.geofenceId;
        }
        if (form.geofence) {
            params.geofence = form.geofence;
        }

        if (isEditMode) {
            setSubmitting(true);
            dispatch(updateStaffProfile(editingStaff.id, params, (res, err) => {
                setSubmitting(false);
                if (res && res.show && res.show.type === "success") {
                    if (profileFile) {
                        dispatch(uploadStaffPhoto(editingStaff.id, profileFile, () => {
                            dispatch(getStaffListV3({ page: 1, limit: 20 }, () => { }));
                        }));
                    } else if (pendingPhotoRemoval) {
                        dispatch(deleteStaffPhoto(editingStaff.id, () => {
                            dispatch(getStaffListV3({ page: 1, limit: 20 }, () => { }));
                        }));
                    }
                    handleClose();
                    onStaffAdded?.(editingStaff);
                    dispatch(getStaffListV3({ page: 1, limit: 20 }, () => { }));
                    dispatch(getStaffStats());
                } else if (err) {
                    const errMsg = extractErrorMessage(err, "Failed to update staff");
                    notification.error({
                        message: "Error",
                        description: errMsg,
                    });
                }
            }));
            return;
        }

        setSubmitting(true);
        dispatch(addStaff(params, (res, err) => {
            setSubmitting(false);
            const isSuccess = (res && res.show && res.show.type === "success") ||
                (res && !err && !res.error && res.status !== false && res.success !== false && (res.data || res.id));
            if (isSuccess) {
                const newStaff = res?.data ?? res;
                notification.destroy();
                setCreatedStaff(newStaff);
                setSuccessVisible(true);
                // Hide the drawer right away — only the success modal (a
                // separate floating Modal, not inside the Drawer) should be
                // visible from here. Not handleClose(), since that also
                // resets successVisible back to false.
                onClose();

                const newId = newStaff?.id ?? newStaff?.userId ?? res?.id ?? res?.userId;
                const staffPayload = {
                    ...(typeof newStaff === "object" ? newStaff : {}),
                    id: newId,
                    userId: newId,
                    name: form.name,
                    role: form.role?.[0] || "Operation",
                    roles: form.role,
                    createdAt: newStaff?.createdAt || new Date().toISOString(),
                    image: form.profile || newStaff?.image || "",
                    avatar: form.profile || newStaff?.avatar || "",
                };

                // Dispatch to Redux so lastAddedStaff has the new staff member with their image immediately
                dispatch(addStaffSuccess({ ...(typeof res === "object" ? res : {}), data: staffPayload }));

                onStaffAdded?.(staffPayload);

                // Refresh the staff list so the table picks up the new row
                dispatch(getStaffListV3({ page: 1, limit: 20 }, () => { }));

                // Refresh the stats from stats api so staff distribution counts update immediately
                dispatch(getStaffStats({}, (statsRes) => {
                    const statsPayload = statsRes?.data ?? statsRes;
                    if (statsPayload) {
                        onStaffAdded?.(statsPayload);
                    }
                }));

                // Fire-and-forget alongside addStaff — doesn't block the
                // success modal. Captures the uploaded photo's URL onto
                // createdStaff and updates Redux/StaffListTable so the image appears immediately.
                if (profileFile) {
                    dispatch(uploadStaffPhoto(newId, profileFile, (percent, response) => {
                        if (percent === 100) {
                            const uploadedImage = response?.data?.image ?? response?.data?.data?.image ?? response?.image ?? response?.data?.avatar;
                            if (uploadedImage) {
                                setCreatedStaff((prev) => ({ ...prev, image: uploadedImage, avatar: uploadedImage }));
                                const updatedStaffPayload = {
                                    ...staffPayload,
                                    id: newId,
                                    userId: newId,
                                    image: uploadedImage,
                                    avatar: uploadedImage,
                                };
                                dispatch(addStaffSuccess({ ...(typeof res === "object" ? res : {}), data: updatedStaffPayload }));
                            }
                            // Refresh the staff list once photo upload completes on backend
                            dispatch(getStaffListV3({ page: 1, limit: 20 }, () => { }));
                        }
                    }));
                }
            }
        }));
    };

    // "COMPLETE PROFILE" on the success modal — navigate to the same
    // StaffProfilePage view StaffListTable's "View" action uses (via the
    // /user-management/staff-profile route), seeded with setUserDetails from
    // what was just entered/returned here — mirrors StaffListTable.jsx's
    // handleViewProfile(row) exactly, instead of the old flat
    // setUserProfileDetailsV3 + /user-management/edit-profile route (that
    // page reads userRowDetails.id and fetches the rest itself via
    // getStaffProfile, so nothing else needs seeding).
    const handleCompleteProfile = () => {
        const newId = createdStaff?.id ?? createdStaff?.userId;
        const phone = form.mobile;

        dispatch(setUserDetails({
            id: newId,
            name: form.name,
            phone,
            email: form.email || "",
            image: createdStaff?.image || form.profile || "",
            avatar: createdStaff?.image || form.profile || "",
            role: form.role?.[0] || "Teacher",
            access: form.access || "",
            joinedOn: createdStaff?.createdAt || "",
            status: "Active",
        }));

        setSuccessVisible(false);
        handleClose();
        history.push(`${basePath}/staff-profile`);
    };

    // const togglePermission = permission => {
    //     setForm(prev => ({
    //         ...prev,
    //         permissions: {
    //             ...prev.permissions,
    //             [permission]: {
    //                 checked:
    //                     !prev.permissions[permission].checked,
    //                 access:
    //                     !prev.permissions[permission].checked
    //                         ? "view"
    //                         : "",
    //             },
    //         },
    //     }));
    // };

    // const changePermissionAccess = (
    //     permission,
    //     access
    // ) => {
    //     setForm(prev => ({
    //         ...prev,
    //         permissions: {
    //             ...prev.permissions,
    //             [permission]: {
    //                 checked: true,
    //                 access,
    //             },
    //         },
    //     }));
    // };

    const resetDrawer = () => {
        setSubmitting(false);
        setStep("form");
        setSubmitted(false);
        setTouched({ mobile: false, email: false });
        setSuccessVisible(false);
        setProfileFile(null);
        setPendingPhotoRemoval(false);
        setStagedImgUrl(null);
        setStagedImageFile(null);
        setProfilePicModalOpen(false);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
        setForm({
            ...defaultForm,
            mobile: "+91",
        });
    };
    const handleClose = () => {
        resetDrawer();
        onClose();
    };

    const handleProfilePicCancel = () => {
        setProfilePicModalOpen(false);
        setStagedImgUrl(null);
        setStagedImageFile(null);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
    };

    const handleConfirmPhotoChoice = (callback, buttonCallback) => {
        if (stagedImageFile) {
            setProfileFile(stagedImageFile);
            updateField("profile", stagedImgUrl);
        }
        setPendingPhotoRemoval(false);
        callback?.();
        buttonCallback?.();
        setProfilePicModalOpen(false);
    };

    const handleRemovePhoto = () => {
        setProfileFile(null);
        updateField("profile", "");
        setPendingPhotoRemoval(true);
        setStagedImgUrl(null);
        setStagedImageFile(null);
        if (inputProfilePicRef.current) inputProfilePicRef.current.value = null;
        setProfilePicModalOpen(false);
    };

    const existingPhotoSrc = pendingPhotoRemoval ? "" : (form.profile || (isEditMode ? (editingStaff?.image || editingStaff?.avatar || "") : ""));
    const handleAccessSelect = (accessName) => {
        setForm(prev => ({
            ...prev,
            access: accessName,
        }));

        setAccessDropdownOpen(false);
    };

    const handleGeofenceSelect = (item) => {
        const title = item?.title || item?.geoFenceTitle || item?.name || "";
        const id = item?.id ?? item?.geoFenceId ?? null;
        setForm(prev => ({
            ...prev,
            geofence: title,
            geofenceId: id,
        }));
        setGeofenceDropdownOpen(false);
    };

    const handleAddNewAccess = () => {
        setAccessDropdownOpen(false);
        setCreateAccessOpen(true);
    };

    const handleAccessCreated = (payload) => {
        console.log("New access created:", payload);

        const accessName = payload?.accessName;

        if (accessName) {
            setForm(prev => ({
                ...prev,
                access: accessName,
            }));
        }

        setCreateAccessOpen(false);
    };

    return (
        <>
            <Drawer
                visible={visible}
                placement="right"
                width="32%"
                destroyOnClose
                closable={false}
                className="add-staff-drawer staff-status-drawer"
                onClose={handleClose}
                bodyStyle={{
                    padding: 0,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                }}
            >

                {/* Header */}

                <div className="add-staff-header">
                    <div className="add-staff-title"> {isEditMode ? "Edit staff" : "Add a staff"} </div>
                    <CloseOutlined className="add-staff-close" onClick={handleClose} />
                </div>

                {/* Body */}
                <div className="add-staff-body">

                    {/* Avatar */}
                    <div className="add-avatar-wrapper">

                        <Avatar
                            size={84}
                            src={form.profile || undefined}
                            className="add-avatar"
                            // onClick={step === "form" ? () => setProfilePicModalOpen(true) : undefined}
                            style={{ cursor: "default" }}
                        >
                            {form.profile ? null : form.name.trim() ? (
                                <span className="avatar-initials">{initials}</span>
                            ) : (
                                <img
                                    src={UserDefault}
                                    alt="User"
                                    className="default-avatar-icon"
                                />
                            )}
                        </Avatar>

                        {step === "form" && (
                            <div
                                className="avatar-upload-btn"
                                onClick={() => setProfilePicModalOpen(true)}
                            >
                                <CameraFilled />
                            </div>
                        )}
                    </div>

                    {/* ---------------- Role ---------------- */}

                    <div className="addStaff-field">
                        <div className="addStaff-label role-margin">
                            Role <span className="required">*</span>
                        </div>

                        {step === "form" ? (
                            <UserRoleSelectV3
                                roles={roles}
                                role={form.role}
                                setRole={(value) => updateField("role", value)}
                            />
                        ) : (
                            <div className="preview-role">
                                {form.role.map(role => (
                                    <span
                                        key={role}
                                        className="preview-role-pill"
                                    >
                                        {String(role || "").toLowerCase().includes("operation") ? "Operations" : role}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* ---------------- Name ---------------- */}

                    <div className="addStaff-field">

                        {step === "form" ? (
                            <>
                                <FloatingLabelInput
                                    label="Name"
                                    required
                                    value={form.name}
                                    onChange={(e) => updateField("name", sanitizeName(e.target.value))}
                                    error={submitted && errors.name}
                                    errorMessage="Name is required"
                                />
                            </>
                        ) : (<>
                            <div className="addStaff-label"> Name <span className="required">*</span> </div>
                            <div className="addStaff-value"> {form.name} </div>
                        </>
                        )}
                    </div>

                    {/* ---------------- Mobile ---------------- */}

                    <div className="addStaff-field">

                        {step === "form" ? (
                            <>
                                <label className="PhoneInputV3-label">
                                    Mobile number <span className="required">*</span>
                                </label>
                                <PhoneInput
                                    className="PhoneInputV3"
                                    defaultCountry="IN"
                                    international
                                    countryCallingCodeEditable={false}
                                    placeholder="Enter mobile number"
                                    value={form.mobile || "+91"}
                                    onChange={mobileCountryHandlers.handleChange}
                                    // Keep the entered number when the country changes.
                                    onCountryChange={mobileCountryHandlers.handleCountryChange}
                                    addInternationalOption={true}
                                    countrySelectComponent={PhoneCountrySelect}
                                    // Backspace/Delete next to the locked "+91" must not wipe the number.
                                    numberInputProps={{ onKeyDown: keepCallingCodeOnKeyDown }}
                                    style={{ border: 'none', borderBottom: '1px solid #E6E6E6' }}
                                    // Once it's a 10-digit Indian number (+91XXXXXXXXXX,
                                    // 13 chars total), cap further typing at 11 so it
                                    // can't grow past that — same pattern used across
                                    // the app's other PhoneInput fields (e.g.
                                    // userEditProfileV3.js, userModal.js, studentModal.js).
                                    maxLength={(form.mobile?.startsWith('+91') || form.mobile?.startsWith('91')) && form.mobile?.length === 13 ? 11 : ""}
                                />
                                {((touched.mobile && typedMobileDigits.length > 0 ? errors.mobile : false) || (submitted && errors.mobile)) && (
                                    <div className="floating-error" style={{ textAlign: "right", fontSize: "10px" }}>Enter a valid mobile number</div>
                                )}
                            </>
                        ) : (<>
                            <div className="addStaff-label"> Mobile number <span className="required">*</span> </div>
                            <div className="addStaff-value"> {form.mobile} </div>
                        </>
                        )}
                    </div>

                    {/* ---------------- Email ---------------- */}

                    <div className="addStaff-field">

                        {step === "form" ? (
                            <>
                                <FloatingLabelInput
                                    label="Email ID"
                                    value={form.email}
                                    onChange={(e) => {
                                        updateField("email", e.target.value);
                                        setTouched(prev => ({ ...prev, email: true }));
                                    }}
                                    error={(touched.email || submitted) && errors.email}
                                    errorMessage="Enter a valid email id"
                                    errorAlign="right"
                                    keepLabelColor={true}
                                    keepBorderColor={true}
                                />
                            </>
                        ) : (<>
                            <div className="addStaff-label"> Email ID </div>
                            <div className="addStaff-value"> {form.email || "NA"} </div>
                        </>
                        )}
                    </div>

                    {/* -------------------- Access -------------------- */}

                    <div className="addStaff-field access-field" style={{ zIndex: accessDropdownOpen ? 10 : 1 }}>

                        <div className="addStaff-label">
                            Access
                        </div>

                        {step === "form" ? (
                            <Dropdown
                                trigger={["click"]}
                                open={accessDropdownOpen}
                                onOpenChange={setAccessDropdownOpen}
                                placement="bottomLeft"
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                                overlayClassName="add-staff-access-dropdown"
                                dropdownRender={() => (
                                    <div className="add-staff-access-menu" style={{ width: "100%", minWidth: 260, maxHeight: 250, overflowY: "auto" }}>

                                        {/* ADD NEW ACCESS */}

                                        <div
                                            className="add-staff-new-access"
                                            onClick={handleAddNewAccess}
                                        >
                                            Add new access
                                        </div>

                                        {/* EXISTING ACCESS */}

                                        {ACCESS_DATA.map((accessName) => (
                                            <div
                                                key={accessName}
                                                className={`add-staff-access-option ${form.access === accessName
                                                    ? "selected"
                                                    : ""
                                                    }`}
                                                onClick={() =>
                                                    handleAccessSelect(accessName)
                                                }
                                            >
                                                {accessName}
                                            </div>
                                        ))}

                                    </div>
                                )}
                            >
                                <div className="add-staff-access-select">

                                    <span
                                        className={
                                            form.access
                                                ? "selected-access"
                                                : "placeholder-access"
                                        }
                                    >
                                        {form.access || "Select access"}
                                    </span>

                                    <span className={`access-arrow ${accessDropdownOpen ? "rotated" : ""
                                        }`}>
                                        ›
                                    </span>

                                </div>
                            </Dropdown>
                        ) : (
                            <div className="addStaff-value">
                                {form.access || "NA"}
                            </div>
                        )}

                    </div>

                    {/* -------------------- Geofence -------------------- */}

                    <div className="addStaff-field access-field" style={{ zIndex: geofenceDropdownOpen ? 10 : 1 }}>

                        <div className="addStaff-label">
                            Permanent work location
                        </div>

                        {step === "form" ? (
                            <Dropdown
                                trigger={["click"]}
                                open={geofenceDropdownOpen}
                                onOpenChange={setGeofenceDropdownOpen}
                                placement="bottomLeft"
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                                overlayClassName="add-staff-access-dropdown"
                                dropdownRender={() => (
                                    <div className="add-staff-access-menu" style={{ width: "100%", minWidth: 260, maxHeight: 250, overflowY: "auto" }}>
                                        {geoFenceLoading && (!geoFenceList || geoFenceList.length === 0) ? (
                                            <div style={{ padding: "8px 12px", color: "#8c8c8c", fontSize: "14px" }}>
                                                Loading locations...
                                            </div>
                                        ) : (dropdownLocations && dropdownLocations.length > 0) ? (
                                            dropdownLocations.map((item, index) => {
                                                const title = item?.title || item?.geoFenceTitle || item?.name;
                                                const id = item?.id ?? item?.geoFenceId ?? index;
                                                const isSelected = form.geofenceId === (item?.id ?? item?.geoFenceId) || (form.geofence && form.geofence.toLowerCase() === (title || "").toLowerCase());
                                                return (
                                                    <div
                                                        key={id}
                                                        className={`add-staff-access-option ${isSelected ? "selected" : ""}`}
                                                        onClick={() => handleGeofenceSelect(item)}
                                                    >
                                                        {title}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            <div style={{ padding: "8px 12px", color: "#8c8c8c", fontSize: "14px" }}>
                                                No locations found
                                            </div>
                                        )}
                                    </div>
                                )}
                            >
                                <div className="add-staff-access-select">
                                    <span
                                        className={
                                            form.geofence
                                                ? "selected-access"
                                                : "placeholder-access"
                                        }
                                    >
                                        {form.geofence || "Global"}
                                    </span>

                                    <span className={`access-arrow ${geofenceDropdownOpen ? "rotated" : ""}`}>
                                        ›
                                    </span>
                                </div>
                            </Dropdown>
                        ) : (
                            <div className="addStaff-value">
                                {form.geofence || "Global"}
                            </div>
                        )}

                    </div>

                </div>

                {/* Footer */}

                <div className="add-staff-footer">

                    {step === "form" ? (
                        <>
                            <div
                                className={`clear-btn`}
                                onClick={isFormEmpty ? undefined : clearForm}
                                style={{
                                    cursor: isFormEmpty ? "not-allowed" : "pointer",
                                    opacity: isFormEmpty ? 0.5 : 1,
                                    pointerEvents: isFormEmpty ? "none" : "auto",
                                    transition: "opacity 0.2s ease",
                                }}
                            >
                                CLEAR
                            </div>

                            <Button
                                type="primary"
                                disabled={!isFormValid}
                                onClick={handleAdd}
                            >
                                {isEditMode ? "SAVE" : "ADD"}
                            </Button>
                        </>
                    ) : (
                        <>
                            <div
                                className="clear-btn"
                                onClick={handleEdit}
                            >
                                EDIT
                            </div>

                            <button className="confirm-btn"
                                onClick={handleConfirm}
                                disabled={submitting}
                                style={submitting ? { opacity: 0.7, cursor: "not-allowed" } : undefined}
                            >
                                {submitting ? "CONFIRMING..." : "CONFIRM"}
                            </button>
                        </>
                    )}
                </div>
            </Drawer>
            <CreateAccessDrawer
                open={createAccessOpen}
                mode="create"
                onClose={() => setCreateAccessOpen(false)}
                onCreate={handleAccessCreated}
            />
            <AddStaffSuccessModal
                visible={successVisible}
                staffName={form.name}
                role={form.role?.[0]?.toLowerCase()?.includes("operation") ? "Operations" : form.role?.[0]}
                onCancel={() => {
                    setSuccessVisible(false);
                    handleClose();
                }}
                onComplete={handleCompleteProfile}
            />
            <UserProfilePicChangeModal
                showModal={profilePicModalOpen}
                setProfilePicModal={setProfilePicModalOpen}
                setShowConfirmModal={handleRemovePhoto}
                setFlag={() => { }}
                handleOk={() => inputProfilePicRef?.current?.click()}
                src={existingPhotoSrc}
                ref={inputProfilePicRef}
                setfileInputValue={(file) => setStagedImageFile(file)}
                handleCancel={handleProfilePicCancel}
                onOk={(callback, buttonCallback) => handleConfirmPhotoChoice(callback, buttonCallback)}
                setImgUrl={setStagedImgUrl}
                imgUrl={stagedImgUrl}
                imageFile={stagedImageFile}
                setImageFile={setStagedImageFile}
            />
        </>
    );
};

export default AddStaffDrawer;